/**
 * AIWARI auth screen (R232q71).
 *
 * Заменяет старый splash + LoginPanel. Промежуточный экран «Storm Games
 * представляет → Войти / Играть гостем» убран — пользователь сразу видит
 * форму входа. После успешного login (Google OAuth ИЛИ email magic-link
 * ИЛИ email+password) → setStage('realm-select').
 *
 * Гостевой режим намеренно убран: новички не должны случайно играть без
 * сохранения прогресса (см. user feedback 2026-05-07).
 */

import { useEffect, useState } from 'react';
import {
  signInWithEmail,
  signInWithGoogle,
  signInWithPassword,
  signUpWithPassword,
  useAuth,
} from '../game/auth.js';
import { setMusicZone, startMusic } from '../audio/MusicEngine.js';
import { useUI } from '../state/store.js';

type Mode = 'signin' | 'signup' | 'magic';

export function AuthScreen() {
  const setStage = useUI((s) => s.setStage);
  const userId = useAuth((s) => s.userId);
  const authLoading = useAuth((s) => s.loading);

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // R232q71: as soon as session lands → forward to realm-select.
  useEffect(() => {
    if (userId && !authLoading) {
      setStage('realm-select');
    }
  }, [userId, authLoading, setStage]);

  // Music kickoff on first user gesture.
  useEffect(() => {
    setMusicZone('menu');
    const onGesture = () => {
      startMusic();
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
    window.addEventListener('pointerdown', onGesture);
    window.addEventListener('keydown', onGesture);
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
  }, []);

  const handleGoogle = async () => {
    setBusy(true); setErr(null); setMsg(null);
    const r = await signInWithGoogle();
    if (!r.ok) { setErr(r.error ?? 'Не удалось открыть Google'); setBusy(false); }
    // Successful → page redirects, useEffect picks up userId.
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setErr('Заполни email и пароль'); return; }
    setBusy(true); setErr(null); setMsg(null);
    const r = await signInWithPassword(email, password);
    if (!r.ok) {
      setErr(r.error ?? 'Не удалось войти');
      setBusy(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setErr('Заполни email и пароль'); return; }
    if (password.length < 8) { setErr('Пароль должен быть не короче 8 символов'); return; }
    setBusy(true); setErr(null); setMsg(null);
    const r = await signUpWithPassword(email, password);
    if (!r.ok) {
      setErr(r.error ?? 'Не удалось зарегистрироваться');
      setBusy(false);
      return;
    }
    if (r.needsVerification) {
      setMsg('Проверь почту — мы отправили письмо для подтверждения.');
      setBusy(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setErr('Заполни email'); return; }
    setBusy(true); setErr(null); setMsg(null);
    const r = await signInWithEmail(email);
    if (!r.ok) {
      setErr(r.error ?? 'Не удалось отправить письмо');
    } else {
      setMsg(`Ссылка для входа отправлена на ${email}. Проверь почту.`);
    }
    setBusy(false);
  };

  return (
    <div
      className="absolute inset-0 z-50 flex flex-col items-center justify-center text-aiwari-fg overflow-hidden p-4"
      style={{ background: '#000' }}
    >
      {/* R232q86zh8: WoCC-style landing hero — a REAL in-game Northshire shot
          (updated with every milestone) instead of the abstract splash art. */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${import.meta.env.BASE_URL}landing-bg.jpg)`,
          animation: 'kenburns 30s ease-in-out infinite alternate',
          opacity: 0.8,
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 110% 80% at 50% 30%, rgba(13,16,36,0.20) 0%, rgba(5,6,16,0.55) 60%, rgba(0,0,0,0.85) 100%)',
        }}
      />

      <div className="relative z-10 w-full max-w-md" style={{ animation: 'fadein 0.8s' }}>
        {/* Compact title */}
        <div className="text-center mb-6">
          <div className="text-[10px] tracking-[0.4em] text-aiwari-fgDim uppercase">Storm Games</div>
          <h1
            className="text-[30px] sm:text-[42px] leading-none font-black"
            style={{
              background: 'linear-gradient(135deg, #FFE9A6 0%, #E8C173 35%, #B27F2F 65%, #7C5615 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 4px 12px rgba(232,193,115,0.4))',
            }}
          >
            WORLD OF VIBE CRAFT
          </h1>
          <div className="text-[12px] tracking-[0.25em] text-[#E8C173] mt-1">КЛАССИЧЕСКАЯ MMORPG ПРЯМО В БРАУЗЕРЕ</div>
          <div className="text-[11px] text-aiwari-fgDim mt-2">Общий живой мир · Без установки · Играй с любого устройства</div>
          <div className="mx-auto mt-3 h-px w-32" style={{ background: 'linear-gradient(90deg, transparent, #E8C173, transparent)' }} />
        </div>

        {authLoading ? (
          <div className="text-center text-aiwari-fgMuted text-sm py-12">Проверяю аккаунт…</div>
        ) : (
          <div
            className="rounded-2xl p-6 sm:p-7"
            style={{
              background: 'linear-gradient(180deg, rgba(22,18,16,0.92), rgba(11,9,7,0.92))',
              border: '1px solid #5C4520',
              backdropFilter: 'blur(6px)',
              boxShadow: '0 0 32px rgba(0,0,0,0.5), inset 0 0 16px rgba(201,169,106,0.06)',
            }}
          >
            {/* Mode tabs */}
            <div className="flex justify-center gap-1 mb-5 text-[11px] tracking-[0.2em] uppercase" style={{ fontFamily: "'Spectral SC', serif" }}>
              {(['signin', 'signup', 'magic'] as Mode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setMode(m); setErr(null); setMsg(null); }}
                  className={`px-3 py-1.5 transition-all ${mode === m ? 'text-[#E8C173] border-b-2 border-[#E8C173]' : 'text-[#9C8E72] hover:text-[#C4B391] border-b-2 border-transparent'}`}
                >
                  {m === 'signin' ? 'Вход' : m === 'signup' ? 'Регистрация' : 'Magic Link'}
                </button>
              ))}
            </div>

            {/* Google OAuth — full-width primary on every mode. */}
            <button
              type="button"
              disabled={busy}
              onClick={handleGoogle}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg bg-white text-[#1F1813] hover:bg-amber-50 transition-all disabled:opacity-50 mb-4"
              style={{ fontFamily: 'Cinzel, serif', fontWeight: 700, letterSpacing: '0.05em' }}
            >
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z" />
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4.1-3.9 5.5l6.2 5.2c-.4.4 6.4-4.7 6.4-14.7 0-1.3-.1-2.7-.4-3.5z" />
              </svg>
              <span>Войти через Google</span>
            </button>

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, #5C4520, transparent)' }} />
              <span className="text-[10px] tracking-[0.3em] text-[#9C8E72] uppercase">или</span>
              <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, #5C4520, transparent)' }} />
            </div>

            <form onSubmit={mode === 'signin' ? handleSignIn : mode === 'signup' ? handleSignUp : handleMagicLink} className="space-y-3">
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#9C8E72] mb-1" style={{ fontFamily: "'Spectral SC', serif" }}>Email</label>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 rounded-lg bg-[#0B0907] border border-[#5C4520] text-[#E8DCC0] placeholder-[#5C4520] focus:outline-none focus:border-[#C9A96A] transition disabled:opacity-50"
                  style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}
                />
              </div>

              {(mode === 'signin' || mode === 'signup') && (
                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-[#9C8E72] mb-1" style={{ fontFamily: "'Spectral SC', serif" }}>Пароль</label>
                  <input
                    type="password"
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={busy}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 rounded-lg bg-[#0B0907] border border-[#5C4520] text-[#E8DCC0] placeholder-[#5C4520] focus:outline-none focus:border-[#C9A96A] transition disabled:opacity-50"
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}
                  />
                  {mode === 'signup' && (
                    <div className="text-[10px] text-[#9C8E72] mt-1">Не короче 8 символов.</div>
                  )}
                </div>
              )}

              {err && <div className="text-[12px] text-[#F87171] py-1">{err}</div>}
              {msg && <div className="text-[12px] text-[#5BAE57] py-1">{msg}</div>}

              <button
                type="submit"
                disabled={busy}
                className="w-full px-4 py-3 rounded-lg bg-aiwari-bolt text-aiwari-deep hover:bg-amber-300 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-wait"
                style={{ fontFamily: 'Cinzel, serif', fontWeight: 700, letterSpacing: '0.05em' }}
              >
                {busy ? '…' : mode === 'signin' ? 'Войти →' : mode === 'signup' ? 'Зарегистрироваться →' : 'Прислать ссылку →'}
              </button>
            </form>

            {mode === 'signin' && (
              <div className="text-center mt-4 text-[11px] text-[#9C8E72]">
                Нет аккаунта?{' '}
                <button
                  type="button"
                  onClick={() => { setMode('signup'); setErr(null); setMsg(null); }}
                  className="text-[#E8C173] hover:text-[#FFE9A6] underline underline-offset-2"
                >
                  Создай за минуту
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 text-center text-[10px] text-aiwari-fgMuted font-mono">
          build {__BUILD_ID__}
        </div>
      </div>

      <style>{`
        @keyframes fadein {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes kenburns {
          0% { transform: scale(1.05) translate(0, 0); }
          100% { transform: scale(1.15) translate(-2%, 1%); }
        }
      `}</style>
    </div>
  );
}
